import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Avatar,
  Grid,
  Alert,
  Divider,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  Person as PersonIcon,
  Save as SaveIcon,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function AdminProfile() {
  const [profile, setProfile] = useState({
    fullName: '',
    email: '',
    phone: '',
    role: 'admin'
  });
  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Состояния для показа/скрытия паролей
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/users/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setProfile({
        fullName: res.data.fullName || '',
        email: res.data.email || '',
        phone: res.data.phone || '',
        role: res.data.role || 'admin'
      });
      
      // Sync localStorage and notify AppBar
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({ ...stored, ...res.data }));
      window.dispatchEvent(new Event('authChange'));
    } catch (e) {
      console.error('Failed to load profile', e);
      setError('Не удалось загрузить профиль');
    }
  };

  const handleProfileUpdate = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/users/me`, {
        fullName: profile.fullName,
        phone: profile.phone
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

  // Update local storage
  const stored = JSON.parse(localStorage.getItem('user') || '{}');
  const updatedUser = { ...stored, fullName: profile.fullName };
  localStorage.setItem('user', JSON.stringify(updatedUser));
  window.dispatchEvent(new Event('authChange'));

  setSuccess('Профиль успешно обновлен');
  // Reload page to refresh AppBar avatar/name consistently
  setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      console.error('Failed to update profile', e);
      setError(e.response?.data?.error || 'Не удалось обновить профиль');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      if (passwords.newPassword !== passwords.confirmPassword) {
        setError('Пароли не совпадают');
        setLoading(false);
        return;
      }

      if (passwords.newPassword.length < 6) {
        setError('Пароль должен содержать минимум 6 символов');
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/users/me/password`, {
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setPasswords({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      setSuccess('Пароль успешно изменен');
    } catch (e) {
      console.error('Failed to change password', e);
      setError(e.response?.data?.error || 'Не удалось изменить пароль');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        <Paper 
          elevation={3} 
          sx={{ 
            p: { xs: 3, md: 4 },
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: { xs: '16px', md: '32px' },
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <Avatar 
              sx={{ 
                width: 80, 
                height: 80, 
                background: 'linear-gradient(135deg, #ff7675 0%, #d63031 100%)',
                mr: 3,
                boxShadow: '0 4px 16px rgba(214, 48, 49, 0.3)'
              }}
            >
              <PersonIcon sx={{ fontSize: 40 }} />
            </Avatar>
            <Box>
              <Typography 
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#2d3748',
                  fontSize: { xs: '1.5rem', md: '2rem' }
                }}
              >
                Профиль администратора
              </Typography>
              <Typography variant="body2" sx={{ color: '#718096', fontWeight: 500, mt: 0.5 }}>
                {profile.email}
              </Typography>
            </Box>
          </Box>

          {success && <Alert severity="success" sx={{ mb: 3, borderRadius: '12px' }}>{success}</Alert>}
          {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>{error}</Alert>}

          <Divider sx={{ my: 3 }} />
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{
              fontWeight: 700,
              color: '#2d3748',
              fontSize: '1.25rem'
            }}
          >
            Основная информация
          </Typography>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Полное имя"
                value={profile.fullName}
                onChange={(e) => setProfile({ ...profile, fullName: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '& fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.23)'
                    },
                    '&:hover fieldset': {
                      borderColor: '#0984e3'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#0984e3',
                      borderWidth: '2px'
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                value={profile.email}
                disabled
                helperText="Email не может быть изменен"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '&.Mui-disabled': {
                      backgroundColor: 'rgba(0, 0, 0, 0.02)'
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Телефон"
                value={profile.phone}
                onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                placeholder="+375..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '& fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.23)'
                    },
                    '&:hover fieldset': {
                      borderColor: '#0984e3'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#0984e3',
                      borderWidth: '2px'
                    }
                  }
                }}
              />
            </Grid>
          </Grid>

          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleProfileUpdate}
            disabled={loading}
            sx={{
              mb: 4,
              background: 'linear-gradient(135deg, #56ab2f 0%, #2d7a1f 100%)',
              color: '#fff',
              fontWeight: 600,
              borderRadius: '12px',
              textTransform: 'none',
              px: 3,
              boxShadow: '0 4px 14px rgba(45, 122, 31, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #2d7a1f 0%, #56ab2f 100%)',
                boxShadow: '0 6px 20px rgba(45, 122, 31, 0.6)',
                transform: 'translateY(-2px)'
              },
              '&:disabled': {
                background: 'linear-gradient(135deg, #b2bec3 0%, #636e72 100%)',
                color: '#fff',
                opacity: 0.6
              },
              transition: 'all 0.3s ease'
            }}
          >
            Сохранить изменения
          </Button>

          <Divider sx={{ my: 3 }} />

          <Typography 
            variant="h6" 
            gutterBottom
            sx={{
              fontWeight: 700,
              color: '#2d3748',
              fontSize: '1.25rem'
            }}
          >
            Изменить пароль
          </Typography>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type={showCurrentPassword ? 'text' : 'password'}
                label="Текущий пароль"
                value={passwords.currentPassword}
                onChange={(e) => setPasswords({ ...passwords, currentPassword: e.target.value })}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        edge="end"
                      >
                        {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '& fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.23)'
                    },
                    '&:hover fieldset': {
                      borderColor: '#0984e3'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#0984e3',
                      borderWidth: '2px'
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type={showNewPassword ? 'text' : 'password'}
                label="Новый пароль"
                value={passwords.newPassword}
                onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '& fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.23)'
                    },
                    '&:hover fieldset': {
                      borderColor: '#0984e3'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#0984e3',
                      borderWidth: '2px'
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type={showConfirmPassword ? 'text' : 'password'}
                label="Подтвердите новый пароль"
                value={passwords.confirmPassword}
                onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '& fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.23)'
                    },
                    '&:hover fieldset': {
                      borderColor: '#0984e3'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#0984e3',
                      borderWidth: '2px'
                    }
                  }
                }}
              />
            </Grid>
          </Grid>

          <Button
            variant="outlined"
            onClick={handlePasswordChange}
            disabled={loading || !passwords.currentPassword || !passwords.newPassword}
            sx={{
              borderColor: '#0984e3',
              color: '#0984e3',
              fontWeight: 600,
              borderRadius: '12px',
              textTransform: 'none',
              px: 3,
              borderWidth: '2px',
              '&:hover': {
                borderColor: '#0984e3',
                backgroundColor: 'rgba(9, 132, 227, 0.08)',
                borderWidth: '2px'
              },
              '&:disabled': {
                borderColor: '#cbd5e0',
                color: '#cbd5e0'
              }
            }}
          >
            Изменить пароль
          </Button>
        </Paper>
      </Box>
    </Container>
  );
}
